import { useState, useMemo } from "react";
import { Zap, Check, ArrowRight, Leaf, Wallet } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/blocks";
import { E85_FAQ, IMAGES } from "@/data/content";
import { useUI } from "@/context/UIContext";

const FIELD = "w-full bg-hp-dark border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-hp-yellow transition-colors font-stat text-xl";
const LABEL = "block text-xs font-bold tracking-widest uppercase text-neutral-400 mb-2";

export default function E85() {
  const { openLead } = useUI();
  const [km, setKm] = useState(15000);
  const [conso, setConso] = useState(7);
  const [prixSP, setPrixSP] = useState(1.85);
  const [prixE85, setPrixE85] = useState(0.85);
  const [open, setOpen] = useState(0);

  const result = useMemo(() => {
    const litres = (km / 100) * conso;
    const costSP = litres * prixSP;
    const costE85 = litres * 1.25 * prixE85; // ~+25% de consommation à l'E85
    const saving = costSP - costE85;
    return { costSP, costE85, saving: Math.max(0, saving) };
  }, [km, conso, prixSP, prixE85]);

  return (
    <div data-testid="e85-page">
      <Seo title="Conversion E85 / Reprogrammation éthanol FlexFuel | Horsepower Garage"
        description="Conversion E85 FlexFuel : économisez sur votre carburant tout en préservant les performances. Simulateur d'économie, fonctionnement, compatibilité et FAQ. Réseau Horsepower."
        canonical="https://www.horsepowergarage.fr/services/e85"
        jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: E85_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }} />
      <PageHero eyebrow="Éco-performance" title="Conversion E85 FlexFuel"
        subtitle="Roulez à l'éthanol E85, réduisez votre budget carburant et préservez les performances de votre moteur."
        image={IMAGES.taillights} />

      {/* Intro cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Wallet, t: "Économique", d: "L'E85 est nettement moins cher à la pompe que le SP98." },
            { icon: Leaf, t: "Écologique", d: "Un biocarburant renouvelable, issu de matières végétales." },
            { icon: Zap, t: "FlexFuel", d: "Essence, E85 ou mélange des deux lorsque le véhicule le permet." },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.08}>
              <div className="hp-card p-7 h-full">
                <c.icon className="text-hp-yellow mb-4" size={28} />
                <h3 className="font-display font-bold uppercase tracking-tight text-xl">{c.t}</h3>
                <p className="text-neutral-400 mt-2 text-sm leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Simulateur */}
      <section id="simulateur" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 scroll-mt-24">
        <div className="hp-card overflow-hidden" data-testid="e85-simulator">
          <div className="px-6 py-5 border-b border-white/10 bg-black">
            <p className="label-eyebrow">Simulateur d'économie E85</p>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl mt-1">Estimez votre économie annuelle</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 p-6 sm:p-8">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className={LABEL}>Km / an</label><input type="number" className={FIELD} value={km} onChange={(e) => setKm(+e.target.value || 0)} data-testid="e85-km" /></div>
              <div><label className={LABEL}>Conso (L/100km)</label><input type="number" step="0.1" className={FIELD} value={conso} onChange={(e) => setConso(+e.target.value || 0)} data-testid="e85-conso" /></div>
              <div><label className={LABEL}>Prix SP98 (€/L)</label><input type="number" step="0.01" className={FIELD} value={prixSP} onChange={(e) => setPrixSP(+e.target.value || 0)} data-testid="e85-prix-sp" /></div>
              <div><label className={LABEL}>Prix E85 (€/L)</label><input type="number" step="0.01" className={FIELD} value={prixE85} onChange={(e) => setPrixE85(+e.target.value || 0)} data-testid="e85-prix-e85" /></div>
            </div>
            <div className="bg-hp-yellow p-8 flex flex-col justify-center" data-testid="e85-result">
              <p className="text-xs font-bold tracking-widest uppercase text-black/60">Économie estimée / an</p>
              <p className="font-stat font-black text-6xl text-black leading-none mt-2">{Math.round(result.saving)} €</p>
              <div className="mt-5 pt-5 border-t border-black/20 grid grid-cols-2 gap-4 text-black">
                <div><p className="text-xs font-bold uppercase tracking-wide text-black/60">Budget SP98</p><p className="font-stat font-bold text-2xl">{Math.round(result.costSP)} €</p></div>
                <div><p className="text-xs font-bold uppercase tracking-wide text-black/60">Budget E85</p><p className="font-stat font-bold text-2xl">{Math.round(result.costE85)} €</p></div>
              </div>
              <p className="text-[11px] text-black/60 mt-4">Estimation indicative (surconsommation E85 estimée à +25%). Les valeurs réelles varient selon le véhicule et la conduite.</p>
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-center">
          <button onClick={() => openLead({ request_type: "devis", service: "Conversion E85" })} className="hp-cta px-8 py-4 text-sm inline-flex items-center gap-2" data-testid="e85-cta">
            Vérifier la compatibilité de mon véhicule <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="font-display font-bold uppercase tracking-tight text-3xl text-center mb-10">Questions fréquentes</h2>
        <div className="space-y-3">
          {E85_FAQ.map((f, i) => (
            <div key={i} className="hp-card" data-testid={`e85-faq-${i}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 p-5 text-left">
                <span className="font-display font-semibold text-lg">{f.q}</span>
                <span className={`text-hp-yellow text-2xl transition-transform ${open === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {open === i && <p className="px-5 pb-5 text-neutral-400 leading-relaxed">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>
      <CTABand />
    </div>
  );
}
